import { isReference } from "@apollo/client/cache";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client/react";
import {
  Autocomplete,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Theme,
  Tooltip,
  Typography,
  styled,
  useMediaQuery,
} from "@mui/material";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Link as RouterLink } from "react-router-dom";
import { toast } from "react-toastify";
import { hasPermission } from "../../../components";
import { StatefulButton } from "../../../components/ui/muiButtons";
import Permissions from "../../../constants/permissions";
import UserRoles, { Role } from "../../../constants/roles";
import useAuth from "../../../context/UserContext";
import type {
  AccountsV2Query,
  AccountsV2QueryVariables,
  SaveUserMutationVariables,
  VerifyEmailQuery,
  VerifyEmailQueryVariables,
} from "../../../graphql/types";
import { AccountsV2Document, SaveUserDocument, SaveUserMutation, VerifyEmailDocument } from "../../../graphql/types";

import { XIcon } from "@phosphor-icons/react/dist/csr/X";
import { Pill } from "../../../components/ui/Pill";
import { getAllowedRoles, validateEmail } from "../../../lib/Util";

enum VerifyEmailMessages {
  NewEmail = "This is a new user",
  ExistAuth0ButNotLocalDb = "We recognize this user, but it has no access to any accounts in Switchboard yet.",
  ExistLocalDB = "We recognize this user, and they have access to some accounts in Switchboard already.",
  IdPUser = "⋆ This user will use SSO/SAML to log in.",
}

type DefaultValuesFormType = {
  userName: string;
  userEmail: string;
  userPassword: string;
  userConfirmPassword: string;
  isGlobalAccess: boolean;
  roles: Array<Role>;
  urlNames: Array<string>;
};

interface IAddUser {
  open: boolean;
  onClose: () => void;
  accountUrlName?: string;
  email?: string;
  name?: string;
}

const AutocompleteStyled = styled(Autocomplete)(({ theme }) => ({
  margin: `${theme.spacing(2)} 0px ${theme.spacing(1)}`,
})) as typeof Autocomplete;

const AddUser = ({ open, onClose, accountUrlName, email, name }: IAddUser) => {
  const {
    control,
    formState: { isValid },
    handleSubmit,
    setError,
    reset,
    setValue,
    watch,
    getValues,
  } = useForm<DefaultValuesFormType>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      userName: name || "",
      userEmail: email,
      userPassword: "",
      userConfirmPassword: "",
      isGlobalAccess: false,
      roles: [],
      urlNames: accountUrlName ? [accountUrlName] : [],
    },
  });
  const { user } = useAuth();
  const [showAllFields, setShowAllFields] = useState(false);
  const [message, setMessage] = useState<VerifyEmailMessages | null>(null);
  const [idPMessage, setIdPMessage] = useState<string | null>(null);
  const [verifyEmail, { loading: loadingVerifyEmail, data }] = useLazyQuery<
    VerifyEmailQuery,
    VerifyEmailQueryVariables
  >(VerifyEmailDocument, {
    fetchPolicy: "cache-and-network",
  });
  // eslint-disable-next-line react-hooks/incompatible-library
  const isGlobalAccess = watch("isGlobalAccess", false);
  const [saveUser, saveUserState] = useMutation<SaveUserMutation, SaveUserMutationVariables>(SaveUserDocument, {
    onCompleted: (result: SaveUserMutation) => {
      resolveMutation(result);
    },

    onError: () => {
      toast.error(`Error when saving user`);
    },
  });
  const isNewUser = !name && !email;
  const isInUserDetails = email && name;

  const handleClose = () => {
    reset();
    setShowAllFields(false);
    onClose();
    setMessage(null);
  };
  const resolveMutation = (result: SaveUserMutation) => {
    if (result.saveUserV2.__typename === "SaveUserSuccessMutationResult") {
      toast.success("User saved");
      reset();
      handleClose();
    } else if (result.saveUserV2.__typename === "SaveUserErrors") {
      result.saveUserV2.errors?.some((error) => {
        if (error.__typename === "ConflictingEmailError") {
          setError("userEmail", {
            message: "Email already exists",
          });
        } else if (error.__typename === "InsecurePasswordError") {
          setError("userPassword", {
            message: error.passwordErrors?.join(", "),
          });
        }
      });
    }
  };
  const accountsv2Response = useQuery<AccountsV2Query, AccountsV2QueryVariables>(AccountsV2Document);
  const accountsOptions = accountsv2Response.data?.accountsV2?.edges.map((edge) => edge.node.urlName) || [];
  const onSubmit = (formData: DefaultValuesFormType) => {
    if (accountUrlName) {
      // Comes from account page
      saveUser({
        variables: {
          urlNames: formData.urlNames,
          userEmail: formData.userEmail.trim(),
          userName: formData.userName.trim(),
          userPassword: formData.userPassword,
          roles: formData.roles.map((role) => role),
        },
        update: (cache, { data }) => {
          if (data?.saveUserV2.__typename !== "SaveUserSuccessMutationResult") return;
          const userData = data.saveUserV2.user;
          cache.modify({
            id: cache.identify({
              __typename: "Query",
              email: email,
            }),
            fields: {
              account(oldData, { readField }) {
                const newUsersList = [
                  ...oldData.users,
                  {
                    __typename: "AccountLoginDetail",
                    email: userData?.email,
                    name: userData?.name,
                    roles: formData.roles,
                  },
                ];
                newUsersList.sort((a, b) => {
                  const aName = readField("name", a) as string;
                  const bName = readField("name", b) as string;
                  return aName.localeCompare(bName);
                });
                return {
                  ...oldData,
                  users: newUsersList,
                };
              },
            },
          });
        },
      });
    } else if (email && name) {
      // Comes from user details page
      saveUser({
        variables: {
          email: email,
          roles: formData.roles,
          urlNames: formData.urlNames,
        },
        update: (cache, { data }) => {
          if (data?.saveUserV2.__typename !== "SaveUserSuccessMutationResult") return;
          const userData = data.saveUserV2.user;
          cache.modify({
            id: cache.identify({
              __typename: "Query",
              email: email,
            }),
            fields: {
              userV2(oldData) {
                const newAccountsList = [...(userData ? userData.accounts : [])];
                return {
                  ...oldData,
                  accounts: newAccountsList,
                };
              },
            },
          });
        },
      });
    } else {
      // Comes from list users page
      const variables = {
        userEmail: formData.userEmail,
        userPassword: formData.userPassword,
        userName: formData.userName,
        roles: formData.roles.map((role) => role),
        urlNames: undefined as string[] | undefined,
      };
      if (!isGlobalAccess) {
        variables["urlNames"] = formData.urlNames;
      }
      saveUser({
        variables,
        update: (cache, { data }) => {
          if (data?.saveUserV2.__typename !== "SaveUserSuccessMutationResult") return;
          const userData = data.saveUserV2.user;
          cache.modify({
            id: cache.identify({
              __typename: "Query",
              email: email,
            }),
            fields: {
              users(oldData, { readField }) {
                if (isReference(oldData)) {
                  return oldData;
                }
                const newUsersEdges = [
                  {
                    _typename: "UserEdge",
                    node: {
                      __typename: "User",
                      email: userData?.email,
                      name: userData?.name,
                      globalRoles: userData?.globalRoles,
                      id: userData?.id,
                    },
                  },
                  ...oldData.edges,
                ];
                const orderedList = newUsersEdges.sort((a, b) => {
                  const aName = readField("name", a.node) as string;
                  const bName = readField("name", b.node) as string;
                  return aName.localeCompare(bName);
                });

                return {
                  ...oldData,
                  edges: orderedList,
                };
              },
            },
          });
        },
      });
    }
  };
  const handleClickVerifyEmail = async () => {
    const { data, error } = await verifyEmail({ variables: { email: getValues("userEmail") } });
    if (error) {
      console.error("Error when verifying email", error);
      toast.error("Error when verifying email");
      return;
    }
    if (!data?.verifyEmail?.auth0Name && !data?.verifyEmail?.userId) {
      setShowAllFields(true);
      setMessage(VerifyEmailMessages.NewEmail);
    } else if (data?.verifyEmail?.auth0Name && !data?.verifyEmail?.userId) {
      setValue("userName", data.verifyEmail.auth0Name || "");
      setMessage(VerifyEmailMessages.ExistAuth0ButNotLocalDb);
      setIdPMessage(VerifyEmailMessages.IdPUser);
      setShowAllFields(true);
    } else {
      setMessage(VerifyEmailMessages.ExistLocalDB);
      setShowAllFields(false);
    }
  };
  const isSmallSize = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const horizontalForm = false;
  const rolesOption = user.roles ? getAllowedRoles(user.roles)?.map((role) => role) : [];
  const disableVerifyButton = Boolean(message) && message !== VerifyEmailMessages.ExistLocalDB;
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth={horizontalForm ? "md" : "xs"}
      fullScreen={isSmallSize}
      data-testid="add-user-modal"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle variant="heading.four">{isNewUser ? "Add new user" : "Add user to an account"}</DialogTitle>
        <DialogContent>
          <Stack spacing={1} direction="row" sx={{ alignItems: "center", height: "75px" }}>
            <Stack sx={{ width: isInUserDetails ? "100%" : "90%", height: "100%" }}>
              <Controller
                name="userEmail"
                control={control}
                defaultValue=""
                rules={{
                  required: true,
                  validate: isNewUser ? validateEmail : () => undefined,
                }}
                render={({ field: { value, onChange }, fieldState }) => (
                  <TextField
                    fullWidth
                    value={value}
                    onChange={onChange}
                    margin="normal"
                    label="Email"
                    error={Boolean(fieldState.error)}
                    required
                    id="userEmail"
                    type="text"
                    helperText={fieldState.error?.message}
                    autoComplete="off"
                    disabled={!isNewUser || disableVerifyButton}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip id="tooltip" placement="right" arrow title="clear">
                              <IconButton
                                onClick={() => {
                                  setMessage(null);
                                  setShowAllFields(false);
                                  reset();
                                }}
                              >
                                <XIcon />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      },

                      htmlInput: {
                        "aria-label": "Form email",
                      },
                    }}
                  />
                )}
              />
            </Stack>
            {isInUserDetails ? null : (
              <Stack sx={{ pt: "4px" }}>
                <StatefulButton
                  onClick={handleClickVerifyEmail}
                  loading={loadingVerifyEmail}
                  disabled={Boolean(validateEmail(watch("userEmail"))) || disableVerifyButton}
                >
                  Verify
                </StatefulButton>
              </Stack>
            )}
          </Stack>
          {message ? (
            <>
              {message !== VerifyEmailMessages.ExistLocalDB && (
                <Typography sx={{ margin: "10px 0px" }}>{message}</Typography>
              )}
              {message === VerifyEmailMessages.ExistLocalDB && (
                <Typography>
                  {message}{" "}
                  {data?.verifyEmail?.userId && (
                    <Link component={RouterLink} to={`/users/${data?.verifyEmail?.userId}`}>
                      See user
                    </Link>
                  )}
                </Typography>
              )}
            </>
          ) : null}
          {showAllFields || isInUserDetails ? (
            <>
              <Grid container>
                <Controller
                  name="userName"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { value, onChange }, fieldState }) => (
                    <TextField
                      fullWidth
                      value={value}
                      onChange={onChange}
                      margin="normal"
                      error={Boolean(fieldState.error)}
                      required
                      label="Name"
                      id="userName"
                      data-testid="user-name"
                      type="text"
                      helperText={fieldState.error?.message}
                      autoComplete="off"
                      disabled={!isNewUser || message === VerifyEmailMessages.ExistAuth0ButNotLocalDb}
                      autoFocus
                      slotProps={{
                        htmlInput: {
                          "aria-label": "Form name",
                        },
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid container size={horizontalForm ? 5 : 12} sx={{ alignSelf: "flex-start" }}>
                {isNewUser && !accountUrlName && hasPermission(Permissions.ManageGlobalUsers, user) ? (
                  <Grid container size={9} sx={{ alignItems: "center", height: "74.25px" }}>
                    <Grid>
                      <FormLabel>Is global access?</FormLabel>
                    </Grid>
                    <Grid>
                      <Controller
                        name="isGlobalAccess"
                        control={control}
                        render={({ field: props }) => (
                          <Checkbox
                            {...props}
                            checked={props.value}
                            onChange={(e) => props.onChange(e.target.checked)}
                            sx={{ marginLeft: "8px" }}
                            slotProps={{
                              input: { "aria-label": "is global access?" },
                            }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                ) : null}
                <Grid size={12} sx={{ display: "flex", minHeight: "74.25px", alignItems: "center" }}>
                  <Controller
                    name="roles"
                    control={control}
                    rules={{ required: true }}
                    render={({ field: { value, onChange } }) => (
                      <AutocompleteStyled
                        fullWidth
                        multiple
                        value={value}
                        options={rolesOption}
                        getOptionLabel={(option: keyof typeof UserRoles) => UserRoles[option].label}
                        onChange={(event, newValue: Role[]) => {
                          onChange(newValue);
                        }}
                        renderValue={(value, getItemProps) =>
                          value.map((option: Role, index: number) => {
                            const { key, ...tagProps } = getItemProps({ index });
                            return (
                              <Pill
                                key={key || option}
                                {...tagProps}
                                deleteIcon={
                                  <XIcon
                                    style={{
                                      width: "18px",
                                      height: "18px",
                                    }}
                                  />
                                }
                                size="small"
                                role={option}
                                label={UserRoles[option].label}
                              />
                            );
                          })
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={isGlobalAccess ? "Global Roles" : "Roles"}
                            variant="outlined"
                            id="roles"
                            data-testid="roles"
                            aria-label="Form roles"
                          />
                        )}
                      />
                    )}
                  />
                </Grid>
                {!isGlobalAccess ? (
                  <Grid size={12} sx={{ display: "flex", height: "74.25px", alignItems: "center" }}>
                    <Controller
                      name="urlNames"
                      control={control}
                      rules={{ required: true }}
                      render={({ field: { value, onChange } }) => (
                        <AutocompleteStyled
                          fullWidth
                          id="urlNames"
                          multiple
                          onChange={(event, newValue: string[]) => {
                            onChange(newValue);
                          }}
                          value={value}
                          loading={accountsv2Response.loading}
                          options={accountsOptions}
                          renderInput={(params) => <TextField {...params} label="Account" />}
                          disabled={Boolean(accountUrlName)}
                          slotProps={{ listbox: { style: { maxHeight: "20vh" } } }}
                        />
                      )}
                    />
                  </Grid>
                ) : null}
              </Grid>
            </>
          ) : null}
          {idPMessage ? (
            <Typography
              sx={{
                color: "primary20.main",
                marginTop: "20px",
              }}
            >
              {idPMessage}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleClose()} data-testid="cancel-button" variant="outlined">
            Cancel
          </Button>
          <StatefulButton
            type="submit"
            loading={saveUserState.loading}
            disabled={!isValid || (!showAllFields && isNewUser)}
          >
            Confirm
          </StatefulButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export { AddUser };
